import type { StyleSpecification } from 'maplibre-gl';

/** Source ID used for the Protomaps vector tile archive. Exported so future
 *  styling sessions and layer-add helpers can reference a single string. */
export const PROTOMAPS_SOURCE_ID = 'protomaps';

/** Source layers exposed by the Protomaps planet build schema. Only the
 *  handful we currently render are listed; full schema docs at
 *  https://docs.protomaps.com/basemaps/layers. */
export const SOURCE_LAYERS = {
  earth: 'earth',
  water: 'water',
  natural: 'natural',
  landuse: 'landuse',
  roads: 'roads',
} as const;

/** Static layer IDs for the minimal cyberpunk basemap. The player and POI
 *  layers are appended at runtime in `map-view.tsx`. */
export const LAYER_IDS = {
  background: 'bg',
  earth: 'earth',
  water: 'water',
  park: 'park',
  roadMinor: 'road-minor',
  roadMedium: 'road-medium',
  roadMajor: 'road-major',
  roadHighway: 'road-highway',
} as const;

/** Design-token-derived palette. Values inlined here because MapLibre style
 *  specs can't read CSS custom properties at evaluation time — the engine
 *  parses these strings on the worker. Keep in sync with tokens.css if those
 *  change; this is the only place colors are duplicated. */
const C = {
  bgVoid: '#06070a', // var(--sf-void)
  bgBase: '#0a0d12', // var(--sf-base)
  earth: '#0c1218', // mid-tone between sf-base and sf-panel
  water: 'rgba(95,208,211,0.10)', // var(--data) faded
  waterEdge: 'rgba(95,208,211,0.28)',
  park: 'rgba(127,196,108,0.07)', // var(--action) faded
  roadMinor: '#1a222c', // var(--line-faint)
  roadMedium: '#243646',
  roadMajor: '#34414f', // var(--line-strong)
  roadHighway: '#3a8a8c', // var(--data-dim)
} as const;

/** Construct a minimal cyberpunk MapLibre style backed by a Protomaps
 *  PMTiles archive at `tilesUrl`. `tilesUrl` must be the full HTTPS URL of
 *  the archive (e.g. the deployed Worker's `*.workers.dev/colorado.pmtiles`);
 *  the `pmtiles://` scheme is added here so the registered PMTiles protocol
 *  can intercept fetches. */
export function cyberpunkStyleMinimal(tilesUrl: string): StyleSpecification {
  return {
    version: 8,
    glyphs: "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    sources: {
      [PROTOMAPS_SOURCE_ID]: {
        type: 'vector',
        url: `pmtiles://${tilesUrl}`,
        attribution: '© OpenStreetMap contributors',
      },
    },
    layers: [
      {
        id: LAYER_IDS.background,
        type: 'background',
        paint: { 'background-color': C.bgBase },
      },
      {
        id: LAYER_IDS.earth,
        type: 'fill',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.earth,
        paint: { 'fill-color': C.earth },
      },
      {
        id: LAYER_IDS.park,
        type: 'fill',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.landuse,
        filter: ['in', ['get', 'kind'], ['literal', ['park', 'cemetery', 'forest', 'wood']]],
        paint: { 'fill-color': C.park },
      },
      {
        id: LAYER_IDS.water,
        type: 'fill',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.water,
        paint: {
          'fill-color': C.water,
          'fill-antialias': true,
        },
      },
      {
        id: 'water-glow',
        type: 'line',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.water,
        minzoom: 12,
        paint: {
          'line-color': 'rgba(95,208,211,0.4)',
          'line-width': 0.5,
          'line-blur': 1,
        },
      },
      {
        id: LAYER_IDS.roadMinor,
        type: 'line',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.roads,
        filter: ['in', ['get', 'kind'], ['literal', ['minor_road', 'other', 'path']]],
        minzoom: 13,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': C.roadMinor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.6, 16, 1.4, 18, 2.6],
        },
      },
      {
        id: LAYER_IDS.roadMedium,
        type: 'line',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.roads,
        filter: ['==', ['get', 'kind'], 'medium_road'],
        minzoom: 11,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': C.roadMedium,
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 14, 1.6, 18, 4],
        },
      },
      {
        id: LAYER_IDS.roadMajor,
        type: 'line',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.roads,
        filter: ['==', ['get', 'kind'], 'major_road'],
        minzoom: 9,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': C.roadMajor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.6, 13, 1.6, 18, 5],
        },
      },
      {
        id: LAYER_IDS.roadHighway,
        type: 'line',
        source: PROTOMAPS_SOURCE_ID,
        'source-layer': SOURCE_LAYERS.roads,
        filter: ['==', ['get', 'kind'], 'highway'],
        minzoom: 6,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': C.roadHighway,
          'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 11, 1.4, 16, 4.5, 18, 7],
          'line-opacity': 0.85,
        },
      },
    ],
    // Center / zoom intentionally omitted; the MapView component controls
    // initial camera so the style stays reusable across screens.
  };
}

/** Fallback style used when `VITE_TILES_URL` is the placeholder default —
 *  renders only the background fill so the screen isn't blank during local
 *  dev before the Worker is deployed. */
export function placeholderStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {},
    layers: [
      {
        id: LAYER_IDS.background,
        type: 'background',
        paint: { 'background-color': '#06070a' },
      },
    ],
  };
}

/** Returns true when the configured tiles URL is the example placeholder.
 *  Lets callers swap in `placeholderStyle()` until R2 + Worker are live. */
export function isPlaceholderTilesUrl(url: string): boolean {
  return !url || url.includes('tiles.example.com');
}
